import React, { useState, useEffect, useCallback, useMemo } from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import { Checkbox, Radio } from 'react-bootstrap'
import i18n from 'live/services/i18n'

type Props = {
    phases: Record<string, unknown>[]
    handlePhaseOptions: (PhaseOptions) => void
    drillingSections: Record<string, unknown>[]
}

const CASING = 'casing string'
const DRILLING = 'drill string'

type SelectedColumn = {
    index: number
    targetRun?: number
    newRunName?: string
}

type SelectedPhase = {
    shouldImport?: boolean
    selectedColumns?: Record<number, SelectedColumn>
}

export type PhaseOptions = Record<
    number,
    Record<number, { columnType?: boolean; shouldImport?: boolean }> & { shouldImport?: boolean }
>

const PhasesOptionsForm: React.FC<Props> = ({ phases, handlePhaseOptions, drillingSections }: Props) => {
    const [selectedPhases, setSelectedPhases] = useState<Record<number, SelectedPhase>>({})

    useEffect(() => {
        handlePhaseOptions(selectedPhases)
    }, [handlePhaseOptions, selectedPhases])

    const onCheckPhase = useCallback((index): void => {
        setSelectedPhases(
            (selectedPhases: Record<number, SelectedPhase>): Record<number, SelectedPhase> => {
                const prevVal = selectedPhases?.[index] || ({ shouldImport: false } as SelectedPhase)

                return { ...selectedPhases, [index]: { ...prevVal, shouldImport: !prevVal.shouldImport } }
            }
        )
    }, [])

    const onChangeImportColumn = useCallback((index, columnKey, columnType, value) => {
        setSelectedPhases(selectedPhases => {
            const newColumn = {
                ...(selectedPhases?.[index]?.[columnKey] || {}),
                shouldIgnore: value,
                columnType
            }

            return {
                ...selectedPhases,
                [index]: { ...selectedPhases[index], [columnKey]: newColumn }
            }
        })
    }, [])

    const phasesActiveColumns = useMemo(() => {
        return _.map(phases, phase => {
            const casingStringId = phase?.casing_string_id
            const drillStringId = phase?.drill_string_id

            return {
                ...(phase || {}),
                columns: _.omitBy(phase?.columns, (column, key) => {
                    return (
                        !(column?.type === CASING && key === casingStringId) &&
                        !(column?.type === DRILLING && key === drillStringId)
                    )
                })
            }
        })
    }, [phases])

    return (
        <Container>
            {phasesActiveColumns?.map((phase, index: number) => {
                const shouldImport = selectedPhases[index]?.shouldImport

                return (
                    <div key={'phase_' + index}>
                        <WrapperPhaseTitle>
                            <div>
                                <div>
                                    <Checkbox
                                        style={{ margin: '0px' }}
                                        type="checkbox"
                                        name="description"
                                        checked={shouldImport}
                                        onChange={() => onCheckPhase(index)}
                                        key={index}
                                    >
                                        <Label>
                                            {i18n('Phase')} {index + 1}
                                        </Label>
                                    </Checkbox>
                                </div>
                            </div>
                            {shouldImport && (
                                <>
                                    <Icon>
                                        <div></div>
                                        <i />
                                    </Icon>
                                    <SectionLabel>
                                        {i18n('Section')} {(drillingSections?.length || 0) + index + 1}
                                    </SectionLabel>
                                </>
                            )}
                        </WrapperPhaseTitle>
                        {!_.isEmpty(phase?.columns) && (
                            <div style={{ marginLeft: '20px' }}>
                                {shouldImport && (
                                    <TitleWrapper>
                                        <Title>{i18n('TUBULAR')}</Title>
                                        <Title>{i18n('Select a option')}</Title>
                                    </TitleWrapper>
                                )}
                                {_.map(phase?.columns, (column, columnKey) => (
                                    <div key={'column_' + columnKey}>
                                        {shouldImport && (
                                            <ColumnWrapper>
                                                <ColumnsName>
                                                    {CASING === column?.type && <div>{i18n('Casing string')}</div>}
                                                    {DRILLING === column?.type && <div>{i18n('Drill string')}</div>}
                                                </ColumnsName>
                                                <Options>
                                                    <StyledRadio
                                                        type="radio"
                                                        value={false}
                                                        checked={!selectedPhases?.[index]?.[columnKey]?.shouldIgnore}
                                                        onChange={() =>
                                                            onChangeImportColumn(index, columnKey, column?.type, false)
                                                        }
                                                    >
                                                        {i18n('Import')}
                                                    </StyledRadio>
                                                    <StyledRadio
                                                        type="radio"
                                                        value={true}
                                                        checked={selectedPhases?.[index]?.[columnKey]?.shouldIgnore}
                                                        onChange={() =>
                                                            onChangeImportColumn(index, columnKey, column?.type, true)
                                                        }
                                                    >
                                                        {i18n("Don't import")}
                                                    </StyledRadio>
                                                </Options>
                                            </ColumnWrapper>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )
            })}
        </Container>
    )
}

export default PhasesOptionsForm

const Container = styled.div`
    margin-left: 20px;
    margin-top: 10px;
`

const Label = styled.div`
    font-size: 13px;
    color: #28334A;
    font-weight: 400;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    font-family: SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif;
`

const SectionLabel = styled(Label)`
    font-size: 13px;
    color: #28334A;
    font-weight: 400;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    font-family: SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif;
`

const WrapperPhaseTitle = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 2fr;
    margin-bottom: 15px;
`

const ColumnWrapper = styled.div`
    display: flex;
    height: 30px;
`

const TitleWrapper = styled.div`
    display: flex;
`

const Options = styled.div`
    flex: 1;
    display: flex;
`

const StyledRadio = styled(Radio)`
    flex: 1;
`

const Title = styled.div`
    flex: 1;
    font-weight: bold;
    color: #28334a;
    margin-bottom: 5px;
`

const ColumnsName = styled.div`
    flex: 1;
`

const Icon = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    div:first-child {
        width: 70px;
        height: 2px;
        background-color: gray;
    }
    > i {
        width: 0;
        height: 0;
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-left: 5px solid gray;
    }
`
